import type { DataRecord } from './types';

// Filters for a category's record list: one drop-down per text field. They narrow both the sidebar list and
// the category table. Add a category or a field here to get another filter — the UI is generic.

export interface CategoryFilter {
  /** The record value (`values[field]`) the filter looks at. */
  field: string;
  /** i18n key of the drop-down's label */
  labelKey: string;
}

export const CATEGORY_FILTERS: Record<string, CategoryFilter[]> = {
  quests: [
    { field: 'journalRegion', labelKey: 'data.filters.journalRegion' },
    { field: 'locationText', labelKey: 'data.filters.location' },
  ],
};

/** Chosen value per field; a missing field means "all". */
export type FilterSelection = Record<string, string>;
export const NO_FILTERS: FilterSelection = {};

/** The choice for records that have no value in the field (a real value is never this). */
export const UNSET = '__unset';

export interface FilterOption {
  value: string;
  count: number;
}

function valueOf(r: DataRecord, field: string): string {
  const v = r.values?.[field];
  return typeof v === 'string' ? v.trim() : '';
}

function matches(r: DataRecord, field: string, wanted: string | undefined): boolean {
  if (!wanted) return true;
  const v = valueOf(r, field);
  return wanted === UNSET ? v === '' : v === wanted;
}

export function activeCount(filters: CategoryFilter[], selection: FilterSelection): number {
  return filters.filter((f) => selection[f.field]).length;
}

/** Whether a record passes every chosen filter (`skip` leaves one field out, for counting its options). */
export function passesFilters(
  r: DataRecord,
  filters: CategoryFilter[],
  selection: FilterSelection,
  skip?: string,
): boolean {
  return filters.every((f) => f.field === skip || matches(r, f.field, selection[f.field]));
}

/**
 * The choices for one filter, each with how many of `records` it leaves (given the other filters), so a
 * choice that would show nothing is visible as such. The current choice is kept even when it matches nothing.
 */
export function filterOptions(
  records: DataRecord[],
  filters: CategoryFilter[],
  selection: FilterSelection,
  field: string,
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const r of records) {
    if (!passesFilters(r, filters, selection, field)) continue;
    const v = valueOf(r, field) || UNSET;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const chosen = selection[field];
  if (chosen && !counts.has(chosen)) counts.set(chosen, 0);
  return [...counts]
    .map(([value, count]) => ({ value, count }))
    .sort(
      (a, b) =>
        // "no value" last, the rest alphabetically
        Number(a.value === UNSET) - Number(b.value === UNSET) || a.value.localeCompare(b.value, 'sv'),
    );
}
